import React, { ComponentProps, useEffect } from 'react';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import {
  UseQueryStateParameters,
  urlEncode,
  useQueryState,
} from 'hooks/useQueryState';
import { useScroll } from 'hooks/useScroll';
import {
  Company,
  itemsCount,
} from 'components/features/target-achievement-support/pages/PortfolioAnalysisInvestmentProduct';
import {
  InvestmentTrustType,
  PortfolioAssetType,
  InvestmentTrustFund,
} from 'components/features/target-achievement-support/utils/type';
import {
  INVESTMENT_TRUST_TYPE,
  portfolioTypeToString,
} from 'components/features/target-achievement-support/utils/portfolioInfo';
import { PaginatedList } from './PaginatedList';
import { InvestmentTrustPager } from './InvestmentTrustPager';
import { InvestmentTrustItem } from './InvestmentTrustItem';
import { InvestmentTrustTabs } from '../share/InvestmentTrustTabs';
import { InvestmentTrustDescription } from '../share/InvestmentTrustDescription';
import { DropDown } from '../../../commons/DropDown';

const fromString = (
  str: string
): { tag: 'Just'; value: Company } | { tag: 'Nothing' } => {
  if (str === '選択なし')
    return {
      tag: 'Just',
      value: {
        tag: 'notSelected',
      },
    };
  return {
    tag: 'Just',
    value: {
      tag: 'selected',
      value: str,
    },
  };
};

const toLabel = (company: Company): string => {
  switch (company.tag) {
    case 'notSelected':
      return '選択なし';
    case 'selected':
      return company.value;
  }
};

const toString_ = toLabel;

const selectedInvestmentTrustToString = (type: InvestmentTrustType) => {
  switch (type) {
    case INVESTMENT_TRUST_TYPE['国内ETF'].type:
      return '国内ETF';
    case INVESTMENT_TRUST_TYPE['国内投信'].type:
      return '投資信託';
    case INVESTMENT_TRUST_TYPE['米国ETF'].type:
      return '米国ETF';
  }
};

const Feature = ({
  investmentTrustType,
  className,
}: {
  investmentTrustType: React.ComponentProps<
    typeof InvestmentTrustTabs
  >['selectedInvestmentTrustType'];
  className?: string;
}): React.ReactElement => {
  const getTitle = (type: typeof investmentTrustType): string => {
    const name: string = selectedInvestmentTrustToString(type);
    return `「${name}」の特徴・傾向`;
  };

  const getDescription = (
    type: typeof investmentTrustType
  ): React.ReactElement => {
    switch (type) {
      case INVESTMENT_TRUST_TYPE['国内ETF'].type:
        return (
          <>
            ・コスト（信託報酬）が安い
            <br />
            ・購入できるのは証券取引所の取引時間の間
            <br />
            ・リアルタイムに価格が変動し、株式と同じように取引できる
          </>
        );
      case INVESTMENT_TRUST_TYPE['国内投信'].type:
        return (
          <>
            ・NISAのつみたて投資枠やiDeCo口座で運用可能
            <br />
            ・少額から始められる
            <br />
            ・いつでも購入できる
            <br />
            ・コストはETFと比較すると少しだけ高い
          </>
        );
      case INVESTMENT_TRUST_TYPE['米国ETF'].type:
        return (
          <>
            ・国内で取り扱っていないETFが購入できる
            <br />
            ・購入できるのは米国市場の取引時間の間
            <br />
            ・為替リスクがある
            <br />
            ・国内株式ETFと比較すると、手数料が高め
          </>
        );
    }
  };

  return (
    <>
      <div className={`py-4 bg-white ${className ?? ''}`}>
        <div className="font-bold text-bluegray-700 text-base">
          {getTitle(investmentTrustType)}
        </div>
        <div className="font-normal text-bluegray-700 text-sm mt-2">
          {getDescription(investmentTrustType)}
        </div>
      </div>
    </>
  );
};

const nub = (strs: string[]): string[] => {
  const set = new Set(strs);
  return Array.from(set);
};

type Props = {
  isDetail: boolean;
  type: PortfolioAssetType;
  items: InvestmentTrustFund['itemsByInvestmentType'];
  onClickInvestmentTrustItem: (symbolCode: string) => () => void;
  accounts:
    | {
        tag: 'aggregated';
        value: { aggregateSource: number; brokerName: string }[];
      }
    | { tag: 'notAggregated' };
  TabFilteredItemsDescription: (props: {
    selectedInvestmentTrustType: React.ComponentProps<
      typeof InvestmentTrustTabs
    >['selectedInvestmentTrustType'];
  }) => React.ReactElement;
  selectedInvestmentTrustType: React.ComponentProps<
    typeof InvestmentTrustTabs
  >['selectedInvestmentTrustType'];
  setInvestmentTrustType: React.Dispatch<
    React.SetStateAction<
      React.ComponentProps<
        typeof InvestmentTrustTabs
      >['selectedInvestmentTrustType']
    >
  >;
  headerTitle: string;
  goToDetail?: (
    investmentTrustType: InvestmentTrustType,
    selectedCompany: string
  ) => void;
};

// MEMO: 「〇〇」に投資できる投資信託・ETFのコンポーネント
export const InvestmentTrustItemListBase = ({
  isDetail,
  type,
  items,
  onClickInvestmentTrustItem,
  accounts,
  TabFilteredItemsDescription,
  selectedInvestmentTrustType,
  setInvestmentTrustType,
  headerTitle,
  goToDetail,
}: Props): React.ReactElement => {
  useCustomHeaderTitle(headerTitle);

  const companyEncode: UseQueryStateParameters<Company>['encode'] = state => {
    switch (state.tag) {
      case 'notSelected':
        return urlEncode('ns');
      case 'selected':
        return urlEncode(`s${state.value}`);
    }
  };

  const companyDecode: UseQueryStateParameters<Company>['decode'] = value => {
    if (value.startsWith('ns')) {
      return {
        tag: 'Success',
        value: {
          tag: 'notSelected',
        },
      };
    }
    if (value.startsWith('s')) {
      const value_ = value.slice(1);
      return {
        tag: 'Success',
        value: {
          tag: 'selected',
          value: value_,
        },
      };
    }
    return { tag: 'Failed' };
  };

  const [company, setCompany] = useQueryState<Company, 'company'>(
    companyEncode,
    companyDecode,
    { tag: 'notSelected' },
    'company'
  );

  const filteredItem =
    selectedInvestmentTrustType === 'JP_MF'
      ? items
          .filter(item => item.investmentType === selectedInvestmentTrustType)
          .filter(
            item =>
              company.tag === 'notSelected' ||
              item.salesCompany.includes(company.value)
          )
      : items.filter(
          item => item.investmentType === selectedInvestmentTrustType
        );

  const [selectedIndex, setSelectedIndex] = useQueryState<
    number | null,
    'pageNum'
  >(
    num => urlEncode(`${num === null ? 'nothing' : num}`),
    value => {
      if (value === 'nothing')
        return {
          tag: 'Success',
          value: null,
        };
      const num = Number(value);
      if (isNaN(num)) {
        return { tag: 'Failed' };
      } else {
        return {
          tag: 'Success',
          value: num,
        };
      }
    },
    filteredItem.length > 0 ? 0 : null,
    'pageNum'
  );

  useEffect(() => {
    if (filteredItem.length <= 0) setSelectedIndex(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItem.length]);

  const isAggregateSourceMono =
    accounts.tag === 'notAggregated'
      ? false
      : accounts.value.some(account => account.aggregateSource === 3);
  const itemsLength = itemsCount(items, company);

  const selectedCompany = companyEncode(company);

  const paginatedItem = filteredItem
    .map(
      (item): ComponentProps<typeof InvestmentTrustItem> => {
        return {
          basePrice: item.standardPrice,
          basePriceDate: new Date(item.standardPriceDate),
          isShowTag: item.held,
          returnPercentage: item.oneYearReturnPer,
          reward: item.trustFeePer,
          symbolCode: item.symbol,
          symbolName: item.symbolName,
          investmentTrustType: selectedInvestmentTrustType,
          onClickDetail: onClickInvestmentTrustItem(item.symbol),
        };
      }
    )
    .sort((a, b) => a.reward - b.reward)
    .flatMap((p, i) =>
      isDetail
        ? [<InvestmentTrustItem {...p} key={i} />]
        : i < 3
        ? [<InvestmentTrustItem {...p} key={i} />]
        : []
    );

  useScroll();

  return (
    <>
      <div className="px-4 pt-4 font-bold text-bluegray-700 text-base bg-white">
        「{portfolioTypeToString(type)}」に投資できる投資信託・ETF
      </div>
      <div className="pt-2 px-4 flex flex-row bg-white text-xs">
        <TabFilteredItemsDescription
          selectedInvestmentTrustType={selectedInvestmentTrustType}
        />
      </div>
      <div className="pt-4 px-4 bg-white text-base">
        <InvestmentTrustTabs
          {...{
            selectedInvestmentTrustType,
            setInvestmentTrustType,
            itemsLength,
            className: s =>
              `${s
                .split(' ')
                .filter(c => !/.*flex.*/.test(c))
                .join(' ')} grid grid-cols-3 gap-2`,
          }}
        />
      </div>
      <Feature
        investmentTrustType={selectedInvestmentTrustType}
        className="pt-4 px-8"
      />
      {selectedInvestmentTrustType === 'JP_MF' && (
        <>
          <div className="items-center pt-4 px-4 bg-white">
            <div className="flex flex-col">
              {accounts.tag === 'aggregated' && isAggregateSourceMono && (
                <div className="flex flex-row items-center">
                  <div className="text-sm text-bluegray-700">販売会社：</div>
                  <DropDown
                    {...{
                      classNames: {
                        container: prev =>
                          `${prev
                            .split(' ')
                            .filter(s => !/h-\d+/.test(s))
                            .join(' ')} ml-2 h-7 pl-2`,
                        select: prev => `${prev} text-sm`,
                      },
                      dropdownIcon: 'chevron-down',
                      fromString,
                      setState: setCompany,
                      state: company,
                      states: [
                        { tag: 'notSelected' },
                        ...nub(
                          accounts.value
                            .filter(account => account.aggregateSource === 3)
                            .map(account => account.brokerName)
                        ).map(
                          brokerName =>
                            ({ tag: 'selected', value: brokerName } as const)
                        ),
                      ],
                      toLabel,
                      toString_,
                    }}
                  />
                </div>
              )}
              <div className="ml-auto text-bluegray-400 text-xs pt-1 pr-4">
                ※信託報酬が低い順に表示
              </div>
            </div>
          </div>
        </>
      )}
      <PaginatedList
        {...{
          listClassName: 'flex-col flex gap-4 pt-3 pb-4 px-4 bg-white',
          numberOfItemsAtPage: 10,
          Pager: InvestmentTrustPager,
          key: `${selectedInvestmentTrustType}${
            company.tag === 'selected'
              ? `${company.tag}${company.value}`
              : company.tag
          }`,
          selectedIndex,
          setSelectedIndex,
        }}
        className="pb-4">
        {paginatedItem}
      </PaginatedList>
      {isDetail ? (
        <InvestmentTrustDescription type={type} className="p-4" />
      ) : (
        <>
          {goToDetail !== undefined && (
            <button
              id="target_seeAll"
              className="mx-4 mb-4 border-bluegray-400 border rounded-lg shadow bg-bluegray-100 flex flex-row items-baseline justify-center py-2 px-4 text-base font-bold"
              onClick={() =>
                goToDetail(
                  selectedInvestmentTrustType,
                  encodeURIComponent(selectedCompany.value)
                )
              }
              disabled={itemsLength[selectedInvestmentTrustType] === 0}>
              すべて見る
              <span className="text-xs">
                （{itemsLength[selectedInvestmentTrustType]}件）
              </span>
            </button>
          )}
        </>
      )}
    </>
  );
};
